import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Pagination,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Grid,
  Card,
  Divider,
  Avatar,
  Menu,
  DialogContentText,
  Skeleton,
  ListItemIcon
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import ListIcon from '@mui/icons-material/List';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FacebookIcon from '@mui/icons-material/Facebook';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import FilterListIcon from '@mui/icons-material/FilterList';
import ImageIcon from '@mui/icons-material/Image';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { api } from '../../services/auth';
import Header from '../../components/Header';
import { useNotification } from '../../contexts/NotificationContext';
import { useBrand } from '../../contexts/BrandContext';
import { isWhatsappTemplateSms, openWhatsappTemplateInBusinessSuite } from '../../services/inboxClient';
import { getArchives, addArchive, removeArchive } from '../../services/archiveService';
import { getFavorites, addFavorite, removeFavorite } from '../../services/favoriteService';

// Constants from CSS
const COLORS = {
  primary: '#00808a',
  primaryLight: '#e6f2f3',
  sidebarBg: '#ffffff',
  panelBg: '#f8fafc',
  border: '#e2e8f0',
  textMain: '#1e293b',
  textMuted: '#64748b',
  success: '#22c55e',
  warning: '#f59e0b',
  white: '#ffffff'
};

const getSmsStatus = (sms) => {
  const now = new Date();
  const publishUp = sms.publishUp ? new Date(sms.publishUp) : null;
  const publishDown = sms.publishDown ? new Date(sms.publishDown) : null;

  if (!sms.isPublished) return { label: 'Brouillon', color: COLORS.warning, dotColor: COLORS.warning };
  if (publishUp && publishUp > now) return { label: 'Programmé', color: COLORS.primary, dotColor: COLORS.primary };
  if (publishDown && publishDown < now) return { label: 'Terminé', color: COLORS.textMuted, dotColor: COLORS.textMuted };
  return { label: 'Publié', color: COLORS.success, dotColor: COLORS.success };
};

const DYNAMIC_FIELDS = [
  { tag: '{contactfield=firstname}', label: 'Prénom' },
  { tag: '{contactfield=lastname}', label: 'Nom' },
  { tag: '{contactfield=email}', label: 'Email' },
  { tag: '{contactfield=phone}', label: 'Téléphone' },
  { tag: '{contactfield=company}', label: 'Entreprise' },
  { tag: '{contactfield=city}', label: 'Ville' },
  { tag: '{contactfield=country}', label: 'Pays' },
  { tag: '{contactfield=position}', label: 'Poste/Fonction' },
];

const DynamicFieldsHelper = ({ onCopy }) => (
  <Box sx={{ p: 1, minWidth: 220 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#fff' }}>
      Variables Dynamiques
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', mb: 1.5, opacity: 0.9, color: '#fff' }}>
      Cliquez sur un champ pour copier le tag :
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {DYNAMIC_FIELDS.map((field) => (
        <Box
          key={field.tag}
          onClick={() => onCopy(field.tag)}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 0.75,
            px: 1,
            borderRadius: 1,
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              transform: 'translateX(4px)'
            },
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#fff' }}>
              {field.label}
            </Typography>
            <code style={{ fontSize: '0.7rem', color: '#cbd5e1', background: 'transparent' }}>{field.tag}</code>
          </Box>
          <FileCopyIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
        </Box>
      ))}
    </Box>
  </Box>
);

// SMS Preview Component
const SmsMockup = ({ message, timestamp = 'maintenant', image }) => (
  <Box sx={{
    width: '100%',
    maxWidth: 280,
    bgcolor: COLORS.white,
    borderRadius: 2,
    p: 2,
    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)',
    border: `1px solid ${COLORS.border}`,
  }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: COLORS.textMuted }}>Message</Typography>
      <Typography sx={{ fontSize: '0.7rem', color: COLORS.textMuted }}>{timestamp}</Typography>
    </Box>
    {image && (
      <Box
        component="img"
        src={image}
        sx={{
          width: '100%',
          borderRadius: 1,
          mb: 1,
          maxHeight: 200,
          objectFit: 'cover'
        }}
      />
    )}
    <Typography sx={{ fontSize: '0.85rem', color: COLORS.textMain, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
      {message || 'Votre message SMS apparaîtra ici...'}
    </Typography>
  </Box>
);

const TextMessages = () => {
  const { showNotification } = useNotification();
  const { currentBrand } = useBrand();

  const handleCopyTag = (tag) => {
    navigator.clipboard.writeText(tag);
    showNotification(`${tag} copié !`, 'info');
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 30, 50];

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('modified'); // name | created | modified
  const [sortDirection, setSortDirection] = useState('desc'); // asc | desc
  const [statusFilter, setStatusFilter] = useState('all'); // all | published | draft | favorites | archived
  const [categoryFilter, setCategoryFilter] = useState('all'); // all | sms | facebook | instagram | whatsapp | whatsapp-template
  const [subMenuCollapsed, setSubMenuCollapsed] = useState(true);

  // Recherche synchronisée comme sur la page Campagne
  const [searchInput, setSearchInput] = useState('');

  // Garder le champ de saisie synchronisé avec la recherche appliquée
  useEffect(() => {
    setSearchInput(search || "");
  }, [search]);

  // Lorsque l'utilisateur efface complètement le champ de recherche,
  // on réinitialise automatiquement la recherche appliquée et on recharge la liste
  useEffect(() => {
    const isInputEmpty = (searchInput || '').trim() === '';
    if (isInputEmpty && search !== '') {
      setSearch('');
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]); // On surveille principalement l'input


  const [archivedMessages, setArchivedMessages] = useState([]);
  const [favoritesMessages, setFavoritesMessages] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formName, setFormName] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formPublished, setFormPublished] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null); // { id, name, url }
  const [editImageUrl, setEditImageUrl] = useState(null); // URL de l'image existante lors de l'édition
  const [saving, setSaving] = useState(false);

  // New states for AI creation
  const [creationMode, setCreationMode] = useState(null); // null, 'manual', 'ai'
  const [whatsappTemplateOpening, setWhatsappTemplateOpening] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [canGenerate, setCanGenerate] = useState(true);
  const [editTooltipOpen, setEditTooltipOpen] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [selectedSmsId, setSelectedSmsId] = useState(null);
  const [selectedSmsDetails, setSelectedSmsDetails] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Stats state
  const [smsStats, setSmsStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);

  // État pour la galerie d'images (assets Mautic)
  const [assetGalleryOpen, setAssetGalleryOpen] = useState(false);
  const [assetImages, setAssetImages] = useState([]);
  const [assetGalleryLoading, setAssetGalleryLoading] = useState(false);
  const [assetGalleryPage, setAssetGalleryPage] = useState(1);
  const [assetGalleryHasMore, setAssetGalleryHasMore] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);

  // --- Gestion de la galerie d'images Mautic ---
  const loadMauticAssets = useCallback(
    async (page = 1) => {
      try {
        setAssetGalleryLoading(true);
        const resp = await api.get('/emails/mautic/assets', {
          params: {
            limit: 30,
            start: (page - 1) * 30,
            orderBy: 'dateAdded',
            orderByDir: 'DESC',
          },
        });
        const data = resp?.data || {};
        const assetsObj = data.assets || {};
        const normalized = Object.values(assetsObj).map((asset) => ({
          id: asset.id,
          name: asset.title || asset.originalFileName || `Asset ${asset.id}`,
          url: asset.downloadUrl || asset.path || asset.url,
        }));
        if (data.total != null) {
          const totalPages = Math.ceil(Number(data.total || 0) / 30);
          setAssetGalleryHasMore(page < totalPages);
        } else {
          setAssetGalleryHasMore(normalized.length === 30);
        }
        return normalized;
      } catch (e) {
        console.error('[TEXT-MESSAGES] Erreur chargement assets Mautic:', e);
        showNotification(
          e?.response?.data?.detail || e.message || "Erreur lors du chargement des images Mautic.",
          'error'
        );
        setAssetGalleryHasMore(false);
        return [];
      } finally {
        setAssetGalleryLoading(false);
      }
    },
    [showNotification]
  );


  // Categories support
  const [categories, setCategories] = useState([]);
  const [formCategory, setFormCategory] = useState('');

  // Fetch categories
  useEffect(() => {
    (async () => {
      try {
        const resp = await api.get('/text-messages/categories');
        if (resp.data && resp.data.success) {
          setCategories(resp.data.items || []);
        }
      } catch (e) {
        console.error('Error fetching categories:', e);
      }
    })();
  }, []);

  // Pre-load some assets for message previews
  useEffect(() => {
    loadMauticAssets(1).then(data => {
      if (data && data.length > 0) {
        setAssetImages(data);
      }
    });
  }, [loadMauticAssets]);

  const isArchived = useCallback(
    (smsId) => archivedMessages.some((a) => String(a.data_id) === String(smsId)),
    [archivedMessages]
  );

  const isFavorite = useCallback(
    (smsId) => favoritesMessages.some((f) => String(f.data_id) === String(smsId)),
    [favoritesMessages]
  );

  const fetchTextMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let finalSearch = search || '';

      // Ajouter le filtre de catégorie au search si une catégorie spécifique est sélectionnée
      if (categoryFilter && categoryFilter !== 'all') {
        const categorySearch = `category:${categoryFilter}`;
        finalSearch = finalSearch ? `${finalSearch} ${categorySearch}` : categorySearch;
      }

      const params = {
        page,
        page_size: pageSize,
        search: finalSearch || undefined,
      };

      // Tri côté backend
      if (sortBy) {
        params.sort_field = sortBy;
      }
      if (sortDirection) {
        params.sort_direction = sortDirection;
      }

      if (statusFilter === 'published') {
        params.is_published = true;
      } else if (statusFilter === 'draft') {
        params.is_published = false;
      }

      // Backend-side filters for favorites/archives
      if (statusFilter === 'favorites') {
        params.favorites_only = true;
      } else if (statusFilter === 'archived') {
        params.archived_only = true;
      }

      const resp = await api.get('/text-messages', { params });
      const data = resp?.data || {};
      if (data.success) {
        setItems(data.items || []);
        setTotal(data.total || 0);
      } else {
        setError(data.message || "Impossible de charger les Messages.");
      }
    } catch (e) {
      console.error('[TEXT-MESSAGES] Load error', e);
      setError(e?.response?.data?.detail || e.message || "Erreur lors du chargement des Messages.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, statusFilter, sortBy, sortDirection, categoryFilter]);

  useEffect(() => {
    fetchTextMessages();
  }, [fetchTextMessages]);

  // Prefetch archives & favorites with metaLoading
  useEffect(() => {
    (async () => {
      try {
        const [archives, favoritesData] = await Promise.all([
          getArchives('sms').catch(() => []),
          getFavorites('sms').catch(() => []),
        ]);
        setArchivedMessages(archives);
        setFavoritesMessages(favoritesData);
      } catch (_) { }
      finally { setMetaLoading(false); }
    })();
  }, []);



  const handleArchiveSms = async (smsId) => {
    try {
      await addArchive('sms', smsId);
      setArchivedMessages((prev) => [...prev, { data_type: 'sms', data_id: smsId }]);
      showNotification('Message archivé avec succès', 'success');

      // Si on est sur le filtre "archived", garder l’élément, sinon il peut disparaître au prochain refresh
      if (statusFilter !== 'archived') {
        setItems((prev) => prev.filter((s) => String(s.id) !== String(smsId)));
        setTotal((prev) => Math.max(0, Number(prev || 0) - 1));
      }
    } catch (e) {
      console.error('[TEXT-MESSAGES] Archive error', e);
      showNotification(
        e?.response?.data?.detail || e.message || "Erreur lors de l'archivage du Message.",
        'error'
      );
    }
  };

  const handleRestoreSms = async (smsId) => {
    try {
      await removeArchive('sms', smsId);
      setArchivedMessages((prev) => prev.filter((a) => String(a.data_id) !== String(smsId)));
      showNotification('Message restauré avec succès', 'success');

      if (statusFilter === 'archived') {
        setItems((prev) => prev.filter((s) => String(s.id) !== String(smsId)));
        setTotal((prev) => Math.max(0, Number(prev || 0) - 1));
      }
    } catch (e) {
      console.error('[TEXT-MESSAGES] Restore error', e);
      showNotification(
        e?.response?.data?.detail || e.message || "Erreur lors de la restauration du Message.",
        'error'
      );
    }
  };

  const toggleFavorite = async (smsId) => {
    try {
      if (isFavorite(smsId)) {
        await removeFavorite('sms', smsId);
        setFavoritesMessages((prev) => prev.filter((f) => String(f.data_id) !== String(smsId)));
        showNotification('Message retiré des favoris', 'success');
      } else {
        await addFavorite('sms', smsId);
        setFavoritesMessages((prev) => [...prev, { data_type: 'sms', data_id: smsId }]);
        showNotification('Message ajouté aux favoris', 'success');
      }
    } catch (e) {
      console.error('[TEXT-MESSAGES] Favorite error', e);
      showNotification(
        e?.response?.data?.detail || e.message || "Erreur lors de la mise à jour des favoris.",
        'error'
      );
    }
  };

  const handleToggleStatus = async (smsId) => {
    try {
      const sms = items.find(item => String(item.id) === String(smsId));
      if (!sms) return;

      const newStatus = !sms.isPublished;

      // Utiliser les données locales directement
      const updateData = {
        name: sms.name,
        message: sms.message,
        isPublished: newStatus,
      };

      // Ajouter les champs optionnels s'ils existent
      if (sms.description) updateData.description = sms.description;
      if (sms.language) updateData.language = sms.language;
      if (sms.publishUp) updateData.publishUp = sms.publishUp;
      if (sms.publishDown) updateData.publishDown = sms.publishDown;

      // Gérer la catégorie (peut être un objet ou un ID)
      if (sms.category) {
        updateData.category = typeof sms.category === 'object'
          ? sms.category.id
          : sms.category;
      }

      const resp = await api.put(`/text-messages/${smsId}`, updateData);

      if (resp.data.success) {
        // Mettre à jour localement
        setItems(prev => prev.map(item =>
          String(item.id) === String(smsId)
            ? { ...item, isPublished: newStatus }
            : item
        ));

        // Mettre à jour les détails si c'est le message sélectionné
        if (String(selectedSmsId) === String(smsId) && selectedSmsDetails) {
          setSelectedSmsDetails(prev => ({ ...prev, isPublished: newStatus }));
        }

        showNotification(
          newStatus ? 'Message activé avec succès' : 'Message désactivé avec succès',
          'success'
        );
      } else {
        showNotification(resp.data.message || 'Erreur lors de la mise à jour du statut', 'error');
      }
    } catch (e) {
      console.error('[TEXT-MESSAGES] Toggle status error', e);
      showNotification(
        e?.response?.data?.detail || e.message || "Erreur lors de la mise à jour du statut.",
        'error'
      );
    }
  };


  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const newSearch = searchInput.trim();
    if (newSearch === search) return; // Évite rechargement inutile si pas de changement

    setSearch(newSearch);
    setPage(1);
  };

  const openCreateDialog = () => {
    setEditingItem(null);
    setFormName('');
    setFormMessage('');
    setFormPublished(true);
    setFormCategory('');
    setCreationMode(null);
    setAiPrompt('');
    setCanGenerate(true);
    setCanGenerate(true);
    setSelectedAsset(null);
    setDialogOpen(true);
  };

  const openWhatsappTemplateManager = async (smsOrName = null) => {
    const brandKey = String(currentBrand?.name || '').trim();
    if (!brandKey) {
      showNotification('Sélectionnez une marque active pour gérer les templates WhatsApp.', 'warning');
      return;
    }
    if (whatsappTemplateOpening) return;

    setWhatsappTemplateOpening(true);
    try {
      await openWhatsappTemplateInBusinessSuite({ brand: brandKey, smsOrName });
      if (!smsOrName) {
        setDialogOpen(false);
      }
    } catch (e) {
      showNotification(
        e?.message || 'Impossible d\'ouvrir le gestionnaire de templates WhatsApp.',
        'error'
      );
    } finally {
      setWhatsappTemplateOpening(false);
    }
  };

  const [detailsMenuAnchor, setDetailsMenuAnchor] = useState(null);

  const handleDetailsMenuOpen = (event) => {
    setDetailsMenuAnchor(event.currentTarget);
  };

  const handleDetailsMenuClose = () => {
    setDetailsMenuAnchor(null);
  };

  const handleDetailsEdit = () => {
    handleDetailsMenuClose();
    if (selectedSmsDetails) {
      // selectedSmsDetails peut avoir la structure { sms: {...} } ou directement {...}
      const item = selectedSmsDetails.sms || selectedSmsDetails;
      openEditDialog(item);
    }
  };

  const handleDetailsDelete = () => {
    handleDetailsMenuClose();
    if (selectedSmsDetails) {
      const item = selectedSmsDetails.sms || selectedSmsDetails;
      setItemToDelete(item);
      setDeleteConfirmOpen(true);
    }
  };

  const handleDetailsToggleStatus = () => {
    handleDetailsMenuClose();
    if (selectedSmsId) {
      handleToggleStatus(selectedSmsId);
    }
  };

  const handleDetailsArchive = () => {
    handleDetailsMenuClose();
    if (selectedSmsId) {
      if (isArchived(selectedSmsId)) {
        handleRestoreSms(selectedSmsId);
      } else {
        handleArchiveSms(selectedSmsId);
      }
    }
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      showNotification('Veuillez saisir une description', 'warning');
      return;
    }

    if (!formCategory) {
      showNotification('Veuillez sélectionner une catégorie avant de générer.', 'warning');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await api.post('/text-messages/generate', {
        query: aiPrompt,
        // Catégorie obligatoire pour une génération plus précise
        category: formCategory
      });

      if (response.data.success) {
        // En mode création unique sur cette page, on prend la première suggestion
        const suggestions = response.data.suggestions || [];
        const suggestion = suggestions.length > 0 ? suggestions[0] : response.data;

        if (suggestion.message || suggestion.text) {
          setFormMessage(suggestion.message || suggestion.text);
          if (suggestion.name) {
            setFormName(suggestion.name);
          }

          // Associer automatiquement la catégorie trouvée par l'IA (ou retournée par le backend)
          const cat = suggestion.category || response.data.category;
          if (cat && cat.id) {
            setFormCategory(cat.id);
          }

          showNotification('Message généré avec succès!', 'success');
        } else {
          showNotification('Aucun message généré. Essayez de reformuler votre demande.', 'warning');
        }
      } else {
        const errorMessage = response.data.message || 'Aucun message généré. Essayez de reformuler votre demande.';
        showNotification(errorMessage, 'warning');
      }
    } catch (error) {
      console.error('Error generating SMS:', error);
      const rawDetail = error?.response?.data?.detail;

      let errorMessage = 'Erreur lors de la génération du message';
      if (typeof rawDetail === 'string') {
        errorMessage = rawDetail;
      } else if (Array.isArray(rawDetail)) {
        // FastAPI / Pydantic 422: tableau d'objets {loc, msg, type, input}
        const msgs = rawDetail
          .map((e) => (e && typeof e === 'object' && e.msg ? e.msg : null))
          .filter(Boolean);
        if (msgs.length) {
          errorMessage = msgs.join(' | ');
        }
      } else if (rawDetail && typeof rawDetail === 'object') {
        // Fallback pour un objet unique
        if (rawDetail.msg) {
          errorMessage = rawDetail.msg;
        }
      }

      showNotification(errorMessage, 'error');
    } finally {
      setIsGenerating(false);
      setCanGenerate(false);
    }
  };

  const openEditDialog = (item) => {
    if (isWhatsappTemplateSms(item)) {
      openWhatsappTemplateManager(item);
      return;
    }
    setEditingItem(item);
    setFormName(item.name || '');
    setFormMessage(item.message || item.text || '');
    setFormPublished(Boolean(item.isPublished));
    // Category can be an object (with id) or just an ID
    const catVal = item.category?.id || item.category || '';
    setFormCategory(catVal);
    setSelectedAsset(null);
    setEditImageUrl(null);
    setDialogOpen(true);

    // Extraire l'ID de l'asset depuis le titre (ex: "Mon Message (123)") et charger l'image
    const itemName = item.name || '';
    const idMatch = itemName.match(/\((\d+)\)$/);
    if (idMatch && idMatch[1]) {
      const assetId = parseInt(idMatch[1], 10);
      // Chercher d'abord dans le cache local
      const cachedAsset = assetImages.find(a => a.id === assetId);
      if (cachedAsset) {
        setEditImageUrl(cachedAsset.url);
      } else {
        // Sinon, récupérer via l'API
        api.get('/emails/mautic/assets', {
          params: { limit: 1, start: 0, search: `id:${assetId}` }
        }).then(resp => {
          const data = resp?.data || {};
          const assetsObj = data.assets || {};
          const assetList = Object.values(assetsObj);
          if (assetList.length > 0) {
            const asset = assetList[0];
            const url = asset.downloadUrl || asset.path || asset.url;
            if (url) setEditImageUrl(url);
          }
        }).catch(e => {
          console.warn('[TEXT-MESSAGES] Impossible de charger l\'image de l\'asset:', e);
        });
      }
    }
  };

  const loadSmsStats = async (smsId) => {
    setStatsLoading(true);
    setStatsError(null);
    setSmsStats(null);

    try {
      const resp = await api.get(`/text-messages/${smsId}/stats`);
      const data = resp?.data || {};
      if (data.success) {
        setSmsStats(data.data || null);
      } else {
        setStatsError(data.message || "Impossible de charger les statistiques.");
      }
    } catch (e) {
      console.error('[TEXT-MESSAGES] Stats error', e);
      setStatsError(
        e?.response?.data?.detail || e.message || "Erreur lors du chargement des statistiques."
      );
    } finally {
      setStatsLoading(false);
    }
  };

  const handleSmsSelect = async (smsId) => {
    setSelectedSmsId(smsId);
    setSelectedLoading(true);
    setSelectedError(null);
    setSelectedSmsDetails(null);
    setSmsStats(null);
    setStatsError(null);

    try {
      const resp = await api.get(`/text-messages/${smsId}`);
      const data = resp?.data || {};
      if (data.success) {
        // Mautic renvoie généralement { sms: {...} } pour un SMS unique
        setSelectedSmsDetails(data.data || null);
        // Charger les statistiques en parallèle
        loadSmsStats(smsId);
      } else {
        setSelectedError(data.message || "Impossible de charger les détails du Message.");
      }
    } catch (e) {
      console.error('[TEXT-MESSAGES] Details error', e);
      setSelectedError(
        e?.response?.data?.detail || e.message || "Erreur lors du chargement des détails du Message."
      );
    } finally {
      setSelectedLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSave = async () => {
    if (!formName.trim() || !formMessage.trim() || !formCategory) {
      showNotification('Le titre, le contenu et la catégorie sont obligatoires.', 'warning');
      return;
    }

    setSaving(true);
    try {
      let name = formName.trim();
      if (selectedAsset?.id) {
        // Ajouter ou remplacer l'ID d'asset dans le titre
        const suffix = `(${selectedAsset.id})`;
        const idRegex = /\(\d+\)$/;

        if (idRegex.test(name)) {
          // Remplacer l'ID existant
          name = name.replace(idRegex, suffix);
        } else {
          // Ajouter à la fin
          name = `${name} ${suffix}`.trim();
        }
      }

      const payload = {
        name,
        message: formMessage.trim(),
        isPublished: formPublished,
        category: formCategory || null,
      };

      // Preserve other fields if editing
      if (editingItem) {
        if (editingItem.language) payload.language = editingItem.language;
        if (editingItem.description) payload.description = editingItem.description;
        if (editingItem.publishUp) payload.publishUp = editingItem.publishUp;
        if (editingItem.publishDown) payload.publishDown = editingItem.publishDown;
      }

      let resp;
      if (editingItem && editingItem.id) {
        resp = await api.put(`/text-messages/${editingItem.id}`, payload);
      } else {
        resp = await api.post('/text-messages', payload);
      }

      const data = resp?.data || {};
      if (data.success) {
        showNotification(
          editingItem ? 'Message mis à jour avec succès.' : 'Message créé avec succès.',
          'success'
        );
        setDialogOpen(false);
        // Reload first page after changes for consistency
        setPage(1);
        await fetchTextMessages();
      } else {
        showNotification(data.message || "Une erreur s'est produite.", 'error');
      }
    } catch (e) {
      console.error('[TEXT-MESSAGES] Save error', e);
      showNotification(
        e?.response?.data?.detail || e.message || "Erreur lors de l'enregistrement du Message.",
        'error'
      );
    } finally {
      setSaving(false);
    }
  };



  const handleOpenAssetGallery = async () => {
    setAssetGalleryOpen(true);
    setAssetGalleryPage(1);
    const assets = await loadMauticAssets(1);
    setAssetImages(assets);
  };

  const handleLoadMoreAssets = async () => {
    if (!assetGalleryHasMore || assetGalleryLoading) return;
    const nextPage = assetGalleryPage + 1;
    setAssetGalleryPage(nextPage);
    const more = await loadMauticAssets(nextPage);
    if (more && more.length > 0) {
      setAssetImages((prev) => [...prev, ...more]);
    }
  };

  const handleSelectAsset = (asset) => {
    setSelectedAsset(asset);
    setCanGenerate(true);
    setAssetGalleryOpen(false);
  };

  // Fonction pour uploader une image dans les assets Mautic
  const handleUploadImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      showNotification('Veuillez sélectionner un fichier image valide.', 'error');
      return;
    }

    // Vérifier la taille du fichier (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      showNotification('L\'image est trop volumineuse. Taille maximale: 5MB.', 'error');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const resp = await api.post('/emails/upload_image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const data = resp?.data || {};
      if (data.success && data.url) {
        const newAsset = {
          id: Date.now(), // ID temporaire basé sur le timestamp
          name: file.name,
          url: data.url,
        };

        // Ajouter le nouvel asset en début de liste
        setAssetImages((prev) => [newAsset, ...prev]);

        showNotification('Image uploadée avec succès !', 'success');

        // Optionnel: sélectionner automatiquement l'image uploadée
        setSelectedAsset(newAsset);
        setCanGenerate(true);
      } else {
        showNotification(
          data.message || "Erreur lors de l'upload de l'image.",
          'error'
        );
      }
    } catch (e) {
      console.error('[TEXT-MESSAGES] Upload error', e);
      showNotification(
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e.message ||
        "Erreur lors de l'upload de l'image.",
        'error'
      );
    } finally {
      setUploadingImage(false);
      // Réinitialiser l'input pour permettre de re-uploader le même fichier
      event.target.value = '';
    }
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete?.id) return;

    setDeleting(true);
    try {
      const resp = await api.delete(`/text-messages/${itemToDelete.id}`);
      const data = resp?.data || {};
      if (data.success) {
        showNotification('Message supprimé avec succès.', 'success');
        setDeleteConfirmOpen(false);
        setItemToDelete(null);
        // Reload current page (or previous if became empty)
        await fetchTextMessages();
      } else {
        showNotification(data.message || "Impossible de supprimer le Message.", 'error');
      }
    } catch (e) {
      console.error('[TEXT-MESSAGES] Delete error', e);
      showNotification(
        e?.response?.data?.detail || e.message || "Erreur lors de la suppression du Message.",
        'error'
      );
    } finally {
      setDeleting(false);
    }
  };

  // Items visibles: côté données, les filtres principaux sont gérés par le backend.
  // On ne garde ici qu'un éventuel filtrage dérivé côté UI si besoin.
  const visibleItems = useMemo(() => items || [], [items]);

  const totalPages = useMemo(() => {
    if (!pageSize) return 1;
    return Math.max(1, Math.ceil((total || 0) / pageSize));
  }, [total, pageSize]);

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>

      <Box sx={{ display: 'flex', flex: 1, gap: 0, overflow: 'hidden', px: 3 }}>
        {/* Sub-Menu Sidebar for filters (similar to Campagne page) */}
        {!subMenuCollapsed && (
          <Box
            sx={{
              width: 240,
              flexShrink: 0,
              bgcolor: 'rgba(249, 250, 251, 0.7)',
              borderRight: '1px solid #E5E7EB',
              transition: 'width 0.2s',
              display: 'flex',
              flexDirection: 'column',
              height: '85vh',
              p: 2,
              mr: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  color: '#1F2937',
                }}
              >
                Navigation
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
              }}
            >
              {/* Tous les Messages */}
              <Box
                onClick={() => {
                  setStatusFilter('all');
                  setPage(1);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: statusFilter === 'all' ? '#007B8A' : 'transparent',
                  color: statusFilter === 'all' ? 'white' : '#6B7280',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: statusFilter === 'all' ? '#007B8A' : '#F3F4F6',
                    color: statusFilter === 'all' ? 'white' : '#374151',
                  },
                  mb: 1,
                }}
              >
                <ListIcon sx={{ fontSize: 20 }} />
                <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  Tous les Messages
                </Typography>
              </Box>

              {/* Favoris */}
              <Box
                onClick={() => {
                  const target = statusFilter === 'favorites' ? 'all' : 'favorites';
                  setStatusFilter(target);
                  setPage(1);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: statusFilter === 'favorites' ? '#007B8A' : 'transparent',
                  color: statusFilter === 'favorites' ? 'white' : '#6B7280',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: statusFilter === 'favorites' ? '#007B8A' : '#F3F4F6',
                    color: statusFilter === 'favorites' ? 'white' : '#374151',
                  },
                  mb: 1,
                }}
              >
                <StarIcon sx={{ fontSize: 20 }} />
                <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  Favoris
                </Typography>
              </Box>

              {/* Archivés */}
              <Box
                onClick={() => {
                  const target = statusFilter === 'archived' ? 'all' : 'archived';
                  setStatusFilter(target);
                  setPage(1);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: statusFilter === 'archived' ? '#007B8A' : 'transparent',
                  color: statusFilter === 'archived' ? 'white' : '#6B7280',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: statusFilter === 'archived' ? '#007B8A' : '#F3F4F6',
                    color: statusFilter === 'archived' ? 'white' : '#374151',
                  },
                  mb: 1,
                }}
              >
                <ArchiveIcon sx={{ fontSize: 20 }} />
                <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  Archivés
                </Typography>
              </Box>

              <Box
                sx={{
                  mt: 3,
                  pt: 3,
                  borderTop: '1px solid #E5E7EB',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    color: '#9CA3AF',
                    mb: 2,
                    px: 1.5,
                  }}
                >
                  Statuts
                </Typography>

                <Box
                  onClick={() => {
                    const target = statusFilter === 'published' ? 'all' : 'published';
                    setStatusFilter(target);
                    setPage(1);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: statusFilter === 'published' ? '#007B8A' : 'transparent',
                    color: statusFilter === 'published' ? 'white' : '#6B7280',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: statusFilter === 'published' ? '#007B8A' : '#F3F4F6',
                      color: statusFilter === 'published' ? 'white' : '#374151',
                    },
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10B981' }}
                  />
                  <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    Actifs
                  </Typography>
                </Box>

                <Box
                  onClick={() => {
                    const target = statusFilter === 'draft' ? 'all' : 'draft';
                    setStatusFilter(target);
                    setPage(1);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: statusFilter === 'draft' ? '#007B8A' : 'transparent',
                    color: statusFilter === 'draft' ? 'white' : '#6B7280',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: statusFilter === 'draft' ? '#007B8A' : '#F3F4F6',
                      color: statusFilter === 'draft' ? 'white' : '#374151',
                    },
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#F59E0B' }}
                  />
                  <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    Brouillons
                  </Typography>
                </Box>
              </Box>

              {/* Section Catégories */}
              <Box
                sx={{
                  mt: 3,
                  pt: 3,
                  borderTop: '1px solid #E5E7EB',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    color: '#9CA3AF',
                    mb: 2,
                    px: 1.5,
                  }}
                >
                  Catégories
                </Typography>

                {/* Toutes les catégories */}
                <Box
                  onClick={() => {
                    setCategoryFilter('all');
                    setPage(1);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: categoryFilter === 'all' ? '#007B8A' : 'transparent',
                    color: categoryFilter === 'all' ? 'white' : '#6B7280',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: categoryFilter === 'all' ? '#007B8A' : '#F3F4F6',
                      color: categoryFilter === 'all' ? 'white' : '#374151',
                    },
                    mb: 1,
                  }}
                >
                  <ListIcon sx={{ fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    Toutes
                  </Typography>
                </Box>

                {/* SMS */}
                <Box
                  onClick={() => {
                    setCategoryFilter('sms');
                    setPage(1);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: categoryFilter === 'sms' ? '#007B8A' : 'transparent',
                    color: categoryFilter === 'sms' ? 'white' : '#6B7280',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: categoryFilter === 'sms' ? '#007B8A' : '#F3F4F6',
                      color: categoryFilter === 'sms' ? 'white' : '#374151',
                    },
                    mb: 1,
                  }}
                >
                  <SmartphoneIcon sx={{ fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    Message
                  </Typography>
                </Box>

                {/* Facebook */}
                <Box
                  onClick={() => {
                    setCategoryFilter('facebook');
                    setPage(1);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: categoryFilter === 'facebook' ? '#007B8A' : 'transparent',
                    color: categoryFilter === 'facebook' ? 'white' : '#6B7280',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: categoryFilter === 'facebook' ? '#007B8A' : '#F3F4F6',
                      color: categoryFilter === 'facebook' ? 'white' : '#374151',
                    },
                    mb: 1,
                  }}
                >
                  <FacebookIcon sx={{ fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    Facebook
                  </Typography>
                </Box>

                {/* Instagram */}
                <Box
                  onClick={() => {
                    setCategoryFilter('instagram');
                    setPage(1);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: categoryFilter === 'instagram' ? '#007B8A' : 'transparent',
                    color: categoryFilter === 'instagram' ? 'white' : '#6B7280',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: categoryFilter === 'instagram' ? '#007B8A' : '#F3F4F6',
                      color: categoryFilter === 'instagram' ? 'white' : '#374151',
                    },
                    mb: 1,
                  }}
                >
                  <InstagramIcon sx={{ fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    Instagram
                  </Typography>
                </Box>

                {/* WhatsApp */}
                <Box
                  onClick={() => {
                    setCategoryFilter('whatsapp');
                    setPage(1);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: categoryFilter === 'whatsapp' ? '#007B8A' : 'transparent',
                    color: categoryFilter === 'whatsapp' ? 'white' : '#6B7280',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: categoryFilter === 'whatsapp' ? '#007B8A' : '#F3F4F6',
                      color: categoryFilter === 'whatsapp' ? 'white' : '#374151',
                    },
                    mb: 1,
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    WhatsApp
                  </Typography>
                </Box>

                {/* Templates WhatsApp (Meta → Mautic) */}
                <Box
                  onClick={() => {
                    setCategoryFilter('whatsapp-template');
                    setPage(1);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: categoryFilter === 'whatsapp-template' ? '#075E54' : 'transparent',
                    color: categoryFilter === 'whatsapp-template' ? 'white' : '#6B7280',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: categoryFilter === 'whatsapp-template' ? '#075E54' : '#F3F4F6',
                      color: categoryFilter === 'whatsapp-template' ? 'white' : '#374151',
                    },
                    mb: 1,
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                    Templates WhatsApp
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Main column with list (mirrors email list layout) */}
        <Box sx={{ width: '40%', minWidth: '320px' }}>
          <Paper
            sx={{
              p: 2,
              borderRadius: 2,
              boxShadow: 'none',
              bgcolor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header with toggle, title and CTA */}
            <Box display="flex" alignItems="center" pb={1.5} mb={1.5} gap={1.5}>
              <Tooltip
                title={subMenuCollapsed ? 'Déplier la barre' : 'Replier la barre'}
                placement="bottom"
                arrow
              >
                <IconButton
                  onClick={() => setSubMenuCollapsed(!subMenuCollapsed)}
                  size="small"
                  sx={{
                    color: '#6B7280',
                    border: '1px solid #E5E7EB',
                    borderRadius: '50%',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    '&:hover': { bgcolor: '#F9FAFB' },
                    p: 0.75,
                  }}
                >
                  {subMenuCollapsed ? (
                    <ChevronRightIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <ChevronLeftIcon sx={{ fontSize: 20 }} />
                  )}
                </IconButton>
              </Tooltip>

              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontSize: '1rem', color: '#1F2937', flexGrow: 1 }}
              >
                Text Messages
              </Typography>

              <Tooltip title="Nouveau Text Message" arrow>
                <IconButton
                  onClick={openCreateDialog}
                  size="small"
                  sx={{
                    color: '#6B7280',
                    '&:hover': {
                      color: '#007B8A',
                      bgcolor: '#F3F4F6',
                    },
                  }}
                >
                  <AddIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Search bar */}
            <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Rechercher un message..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchSubmit(e);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography
                    sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500 }}
                  >
                    Trier par:
                  </Typography>
                  <Box sx={{ position: 'relative' }}>
                    <Select
                      value={sortBy}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSortBy(value);
                        setPage(1);
                      }}
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        bgcolor: '#F9FAFB',
                        border: '1px solid #D1D5DB',
                        borderRadius: 2,
                        minWidth: 140,
                        height: 32,
                        '& .MuiSelect-select': {
                          py: 0.75,
                          px: 1.5,
                          pr: 4,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          color: '#374151',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&:hover': {
                          bgcolor: 'white',
                        },
                        '& .MuiSelect-icon': {
                          color: '#6B7280',
                          fontSize: 16,
                        },
                      }}
                    >
                      <MenuItem value="name" sx={{ fontSize: '0.75rem' }}>
                        Nom
                      </MenuItem>
                      <MenuItem value="modified" sx={{ fontSize: '0.75rem' }}>
                        Date de modification
                      </MenuItem>
                      <MenuItem value="created" sx={{ fontSize: '0.75rem' }}>
                        Date de création
                      </MenuItem>
                    </Select>
                  </Box>
                </Box>

                <Tooltip
                  title={sortDirection === 'asc' ? 'Ordre croissant' : 'Ordre décroissant'}
                  arrow
                >
                  <IconButton
                    onClick={() => {
                      const nextDir = sortDirection === 'asc' ? 'desc' : 'asc';
                      setSortDirection(nextDir);
                      setPage(1);
                    }}
                    size="small"
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 2,
                      bgcolor: '#F9FAFB',
                      border: '1px solid #D1D5DB',
                      color: '#6B7280',
                      '&:hover': {
                        bgcolor: '#F3F4F6',
                        color: '#374151',
                      },
                    }}
                  >
                    {sortDirection === 'asc' ? (
                      <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* List */}
            {loading || metaLoading ? (
              <Box sx={{ p: 4, textAlign: 'center', color: COLORS.textMuted }}>
                <CircularProgress size={24} sx={{ color: COLORS.primary, mb: 2 }} />
                <Typography>Chargement des messages...</Typography>
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : visibleItems.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', color: COLORS.textMuted }}>Aucun résultat</Box>
            ) : (
              <>
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  <List disablePadding>
                    {visibleItems.map(item => {
                      const status = getSmsStatus(item);
                      return (
                        <ListItem
                          key={item.id}
                          button
                          onClick={() => handleSmsSelect(item.id)}
                          sx={{
                            borderBottom: 'none',
                            borderLeft: 'none',
                            bgcolor: selectedSmsId === item.id ? COLORS.primaryLight : 'transparent',
                            py: 1.5,
                            position: 'relative',
                            '&:hover .item-actions': { opacity: 1, pointerEvents: 'auto' }
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', width: '100%', position: 'relative' }}>
                            {/* Favorite Icon (Left) */}
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id); }} sx={{ p: 0.5, color: COLORS.textMuted }}>
                              {isFavorite(item.id) ? <StarIcon sx={{ fontSize: '1.1rem', color: COLORS.warning }} /> : <StarBorderIcon sx={{ fontSize: '1.1rem' }} />}
                            </IconButton>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ color: COLORS.textMain, fontSize: '0.9rem' }}>{item.name || `Message ${item.id}`}</Typography>
                            </Box>

                            {/* Inline Actions (Visible on Hover) */}
                            <Box className="item-actions" sx={{ opacity: 0, pointerEvents: 'none', transition: 'opacity 0.2s', display: 'flex', gap: 0.5, position: 'absolute', right: 24, bgcolor: selectedSmsId === item.id ? COLORS.primaryLight : COLORS.white, px: 1, borderRadius: 1 }}>
                              <Tooltip title={item.isPublished ? "Désactiver" : "Activer"}>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleStatus(item.id); }}>
                                  {item.isPublished ? <ToggleOnIcon fontSize="small" sx={{ color: COLORS.primary }} /> : <ToggleOffIcon fontSize="small" sx={{ color: COLORS.textMuted }} />}
                                </IconButton>
                              </Tooltip>
                              {isArchived(item.id) ? (
                                <Tooltip title="Restaurer">
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRestoreSms(item.id); }}>
                                    <UnarchiveIcon fontSize="small" sx={{ color: COLORS.textMuted }} />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Archiver">
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleArchiveSms(item.id); }}>
                                    <ArchiveIcon fontSize="small" sx={{ color: COLORS.textMuted }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title={isWhatsappTemplateSms(item) ? 'Éditer dans Meta Business Suite' : 'Modifier'}>
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditDialog(item); }}>
                                  <EditIcon fontSize="small" sx={{ color: COLORS.textMuted }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Supprimer">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); confirmDelete(item); }}>
                                  <DeleteOutlineIcon fontSize="small" sx={{ color: COLORS.textMuted }} />
                                </IconButton>
                              </Tooltip>
                            </Box>

                            {/* Status Dot */}
                            <Box className="status-dot" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: status.dotColor, flexShrink: 0 }} />
                          </Box>
                        </ListItem>
                      )
                    })}
                  </List>
                </Box>

                <Box sx={{ py: 1.5, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderTop: `1px solid ${COLORS.border}`, bgcolor: COLORS.white }}>
                  <Select
                    size="small"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    sx={{ mr: 2, width: 72 }}
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                  </Select>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, newPage) => setPage(newPage)}
                    color="primary"
                    size="small"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: COLORS.textMuted,
                        '&:hover': {
                          bgcolor: COLORS.panelBg,
                        },
                        '&.Mui-selected': {
                          bgcolor: COLORS.primary,
                          color: 'white',
                          '&:hover': {
                            bgcolor: COLORS.primary,
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </Paper>
        </Box>

        {/* Right column: SMS details / stats */}
        <Box
          sx={{
            flex: 1,
            ml: 3,
            borderRadius: 2,
            border: '1px solid #E5E7EB',
            bgcolor: 'white',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {selectedLoading ? (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <CircularProgress />
            </Box>
          ) : selectedError ? (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Alert severity="error">{selectedError}</Alert>
            </Box>
          ) : !selectedSmsId ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: '#9CA3AF',
                fontSize: '1rem',
              }}
            >
              Sélectionnez un message pour voir les détails
            </Box>
          ) : (
            (() => {
              const raw = selectedSmsDetails || {};
              const sms = raw.sms || raw || {};
              const name = sms.name || `Message ${selectedSmsId}`;
              const message = sms.message || sms.text || '';
              const isPublished = Boolean(sms.isPublished);

              // Extraire l'ID de l'image du titre si présent (ex: "Mon Message (123)")
              let imageUrl = null;
              const idMatch = name.match(/\((\d+)\)$/);
              if (idMatch && idMatch[1]) {
                const assetId = parseInt(idMatch[1], 10);
                const foundAsset = assetImages.find(a => a.id === assetId);
                if (foundAsset) {
                  imageUrl = foundAsset.url;
                }
              }

              return (
                <>
                  {/* Header avec Actions - Fixe en haut */}
                  <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB', flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
                          {name}
                        </Typography>
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 500,
                            bgcolor: isPublished ? '#D1FAE5' : '#F3F4F6',
                            color: isPublished ? '#065F46' : '#4B5563',
                          }}
                        >
                          {isPublished ? 'Actif' : 'Brouillon'}
                        </Box>
                      </Box>

                      <IconButton onClick={handleDetailsMenuOpen} size="small">
                        <MoreVertIcon />
                      </IconButton>
                      <Menu
                        anchorEl={detailsMenuAnchor}
                        open={Boolean(detailsMenuAnchor)}
                        onClose={handleDetailsMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      >
                        <MenuItem onClick={handleDetailsEdit}>
                          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                          {isWhatsappTemplateSms(selectedSmsDetails?.sms || selectedSmsDetails)
                            ? 'Éditer dans Meta Business Suite'
                            : 'Modifier'}
                        </MenuItem>
                        <MenuItem onClick={handleDetailsToggleStatus}>
                          <ListItemIcon>
                            {isPublished ? (
                              <ToggleOnIcon fontSize="small" sx={{ color: COLORS.primary }} />
                            ) : (
                              <ToggleOffIcon fontSize="small" sx={{ color: COLORS.textMuted }} />
                            )}
                          </ListItemIcon>
                          {isPublished ? 'Désactiver' : 'Activer'}
                        </MenuItem>
                        <MenuItem onClick={handleDetailsArchive}>
                          <ListItemIcon>
                            {isArchived(selectedSmsId) ? (
                              <UnarchiveIcon fontSize="small" sx={{ color: COLORS.textMuted }} />
                            ) : (
                              <ArchiveIcon fontSize="small" sx={{ color: COLORS.textMuted }} />
                            )}
                          </ListItemIcon>
                          {isArchived(selectedSmsId) ? 'Restaurer' : 'Archiver'}
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={handleDetailsDelete} sx={{ color: 'error.main' }}>
                          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                          Supprimer
                        </MenuItem>
                      </Menu>
                    </Box>
                  </Box>

                  {/* Tabs - Fixe sous le header */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0, bgcolor: '#F9FAFB' }}>
                    <Tabs
                      value={tabValue}
                      onChange={handleTabChange}
                      sx={{
                        px: 2,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: '#6B7280',
                          '&.Mui-selected': {
                            color: '#007B8A',
                            fontWeight: 600,
                          },
                        },
                        '& .MuiTabs-indicator': {
                          backgroundColor: '#007B8A',
                        },
                      }}
                    >
                      <Tab label="Aperçu" />
                      <Tab label="Statistiques" />
                      <Tab label="Informations" />
                    </Tabs>
                  </Box>

                  {/* Tab Content - Scrollable */}
                  <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
                    {/* Tab 0: Aperçu */}
                    {tabValue === 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, bgcolor: '#F9FAFB', borderRadius: 2, border: `1px dashed ${COLORS.border}` }}>
                          <SmsMockup
                            message={message}
                            image={imageUrl}
                          />
                        </Box>

                        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'white', border: '1px solid #E5E7EB' }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#374151' }}>
                            Contenu du message
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#4B5563', whiteSpace: 'pre-wrap' }}>
                            {message || 'Aucun contenu'}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Tab 1: Statistiques */}
                    {tabValue === 1 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
                          Statistiques d'envoi
                        </Typography>

                        {statsLoading ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2" sx={{ color: '#6B7280' }}>
                              Chargement des statistiques...
                            </Typography>
                          </Box>
                        ) : statsError ? (
                          <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>
                            {statsError}
                          </Alert>
                        ) : smsStats ? (
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Card
                                sx={{
                                  p: 2,
                                  textAlign: 'center',
                                  bgcolor: '#EFF6FF',
                                  border: '1px solid #BFDBFE',
                                  boxShadow: 'none'
                                }}
                              >
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1E40AF', mb: 0.5 }}>
                                  {smsStats.recipients || 0}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6B7280', textTransform: 'uppercase', fontWeight: 600 }}>
                                  Destinataires
                                </Typography>
                              </Card>
                            </Grid>

                            <Grid item xs={6}>
                              <Card
                                sx={{
                                  p: 2,
                                  textAlign: 'center',
                                  bgcolor: '#F3F4F6',
                                  border: '1px solid #D1D5DB',
                                  boxShadow: 'none'
                                }}
                              >
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#374151', mb: 0.5 }}>
                                  {smsStats.sent || 0}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6B7280', textTransform: 'uppercase', fontWeight: 600 }}>
                                  Envois
                                </Typography>
                              </Card>
                            </Grid>

                            <Grid item xs={6}>
                              <Card
                                sx={{
                                  p: 2,
                                  textAlign: 'center',
                                  bgcolor: '#D1FAE5',
                                  border: '1px solid #6EE7B7',
                                  boxShadow: 'none'
                                }}
                              >
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#065F46', mb: 0.5 }}>
                                  {smsStats.delivered || 0}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6B7280', textTransform: 'uppercase', fontWeight: 600 }}>
                                  Délivrés
                                </Typography>
                              </Card>
                            </Grid>

                            <Grid item xs={6}>
                              <Card
                                sx={{
                                  p: 2,
                                  textAlign: 'center',
                                  bgcolor: '#FEE2E2',
                                  border: '1px solid #FCA5A5',
                                  boxShadow: 'none'
                                }}
                              >
                                <Typography variant="h4" sx={{ fontWeight: 700, color: '#991B1B', mb: 0.5 }}>
                                  {smsStats.failed || 0}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#6B7280', textTransform: 'uppercase', fontWeight: 600 }}>
                                  Échecs
                                </Typography>
                              </Card>
                            </Grid>
                          </Grid>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#6B7280', fontStyle: 'italic' }}>
                            Aucune statistique disponible pour ce message.
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Tab 2: Informations */}
                    {tabValue === 2 && (
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'white', border: '1px solid #E5E7EB' }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
                          Informations du message
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#6B7280', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                              Nom
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                              {name}
                            </Typography>
                          </Box>
                          <Divider />
                          <Box>
                            <Typography variant="caption" sx={{ color: '#6B7280', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                              ID
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                              {sms.id}
                            </Typography>
                          </Box>
                          <Divider />
                          <Box>
                            <Typography variant="caption" sx={{ color: '#6B7280', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                              Catégorie
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                              {sms.category?.title || sms.category?.name || sms.category || 'Non classé'}
                            </Typography>
                          </Box>
                          <Divider />
                          <Box>
                            <Typography variant="caption" sx={{ color: '#6B7280', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                              Statut
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                              {isPublished ? 'Actif' : 'Brouillon'}
                            </Typography>
                          </Box>
                          {sms.dateAdded && (
                            <>
                              <Divider />
                              <Box>
                                <Typography variant="caption" sx={{ color: '#6B7280', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                                  Date de création
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                                  {new Date(sms.dateAdded).toLocaleDateString('fr-FR', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </Typography>
                              </Box>
                            </>
                          )}
                          {sms.dateModified && (
                            <>
                              <Divider />
                              <Box>
                                <Typography variant="caption" sx={{ color: '#6B7280', textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                                  Dernière modification
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#111827', fontWeight: 500 }}>
                                  {new Date(sms.dateModified).toLocaleDateString('fr-FR', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </Typography>
                              </Box>
                            </>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </>
              );
            })()
          )}
        </Box>
      </Box>

      {/* --- Unified Create Modal --- */}
      <Dialog
        open={dialogOpen && !editingItem}
        onClose={handleDialogClose}
        maxWidth={!creationMode ? "md" : "lg"}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            height: !creationMode ? 'auto' : '650px',
            maxWidth: !creationMode ? '650px' : '1000px'
          }
        }}
      >
        {!creationMode ? (
          /* Selection Screen */
          <Box sx={{ p: 5 }}>
            <Box sx={{ textAlign: 'center', mb: 5 }}>
              <Typography variant="h4" sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 1, color: COLORS.textMain }}>Comment souhaitez-vous créer ?</Typography>
              <Typography sx={{ color: COLORS.textMuted }}>Choisissez une méthode pour commencer votre message.</Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Box
                  onClick={() => setCreationMode('ai')}
                  sx={{
                    border: `1px solid ${COLORS.border}`, borderRadius: 4, p: 4, cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', borderColor: COLORS.primary, boxShadow: '0 12px 20px -8px rgba(0,128,138,0.15)' }
                  }}
                >
                  <Box sx={{ width: 48, height: 48, mx: 'auto', mb: 2, color: COLORS.primary }}><SmartToyIcon sx={{ fontSize: 40 }} /></Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Créer avec l'IA</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  onClick={() => setCreationMode('manual')}
                  sx={{
                    border: `1px solid ${COLORS.border}`, borderRadius: 4, p: 4, cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', borderColor: COLORS.primary, boxShadow: '0 12px 20px -8px rgba(0,128,138,0.15)' }
                  }}
                >
                  <Box sx={{ width: 48, height: 48, mx: 'auto', mb: 2, color: COLORS.textMain }}><EditIcon sx={{ fontSize: 40 }} /></Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Créer manuellement</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box
                  onClick={whatsappTemplateOpening ? undefined : openWhatsappTemplateManager}
                  sx={{
                    border: `1px solid ${COLORS.border}`, borderRadius: 4, p: 4, cursor: whatsappTemplateOpening ? 'wait' : 'pointer', textAlign: 'center', transition: 'all 0.2s', opacity: whatsappTemplateOpening ? 0.7 : 1,
                    '&:hover': whatsappTemplateOpening ? {} : { transform: 'translateY(-4px)', borderColor: '#25D366', boxShadow: '0 12px 20px -8px rgba(37,211,102,0.2)' }
                  }}
                >
                  <Box sx={{ width: 48, height: 48, mx: 'auto', mb: 2, color: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {whatsappTemplateOpening ? <CircularProgress size={32} sx={{ color: '#25D366' }} /> : <WhatsAppIcon sx={{ fontSize: 40 }} />}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Template WhatsApp</Typography>
                  <Typography variant="caption" sx={{ color: COLORS.textMuted, display: 'block', mt: 1 }}>
                    Ouvre Meta Business Suite
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button onClick={handleDialogClose} sx={{ color: COLORS.textMuted }}>Annuler</Button>
            </Box>
          </Box>
        ) : (
          /* Editor Layout (Split View) */
          <Box sx={{ display: 'flex', height: '100%' }}>
            {/* Left: Form/Prompt */}
            <Box sx={{ flex: 1, p: 4, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ mb: 3 }}>
                {creationMode === 'ai' && (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, bgcolor: COLORS.primaryLight, color: COLORS.primary, borderRadius: 10, fontSize: '0.75rem', fontWeight: 700, mb: 2, textTransform: 'uppercase' }}>
                    <SmartToyIcon sx={{ fontSize: 14 }} /> IA Assistée
                  </Box>
                )}
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{creationMode === 'ai' ? "Générer avec l'IA" : "Créer un message"}</Typography>
                <Typography variant="body2" sx={{ color: COLORS.textMuted, mt: 0.5 }}>
                  {creationMode === 'ai' ? "Décrivez le message que vous souhaitez envoyer." : "Remplissez les détails de votre message."}
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                {creationMode === 'ai' ? (
                  <Box sx={{ bgcolor: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 2, p: 2, mb: 2 }}>
                    {/* Sélection de catégorie (obligatoire pour IA) */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>
                        Catégorie <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <Select
                        fullWidth
                        size="small"
                        value={formCategory}
                        onChange={e => setFormCategory(e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                          if (selected === '') {
                            return <em style={{ color: COLORS.textMuted, fontStyle: 'normal' }}>Sélectionner une catégorie</em>;
                          }
                          const cat = categories.find(c => c.id === selected);
                          return cat ? (cat.title || cat.name) : selected;
                        }}
                      >
                        <MenuItem value="">
                          <em>Sélectionner une catégorie</em>
                        </MenuItem>
                        {categories
                          .filter(cat =>
                            ['facebook', 'whatsapp', 'instagram', 'sms'].includes(
                              (cat.title || cat.name || '').toLowerCase()
                            )
                          )
                          .map(cat => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.title || cat.name || `Catégorie ${cat.id}`}
                            </MenuItem>
                          ))}
                      </Select>
                    </Box>

                    <TextField
                      fullWidth multiline rows={4} variant="standard"
                      placeholder="ex: Créer un message pour une promo de -50% sur les chaussures de sport ce weekend..."
                      InputProps={{ disableUnderline: true }}
                      value={aiPrompt}
                      onChange={e => {
                        setAiPrompt(e.target.value);
                        setCanGenerate(true);
                      }}
                    />

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>Image (optionnel)</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          value={selectedAsset ? `${selectedAsset.name} (ID: ${selectedAsset.id})` : ''}
                          fullWidth
                          size="small"
                          InputProps={{
                            readOnly: true,
                            disableUnderline: true,
                            sx: { bgcolor: '#F9FAFB', borderRadius: 1 }
                          }}
                          variant="standard"
                          placeholder="Aucune image sélectionnée"
                        />
                        <Tooltip title="Choisir une image">
                          <IconButton
                            onClick={() => {
                              handleOpenAssetGallery();
                              setCanGenerate(true);
                            }}
                            sx={{
                              border: `1px solid ${COLORS.border}`,
                              borderRadius: 1,
                              color: COLORS.primary
                            }}
                          >
                            <ImageIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Button
                      fullWidth
                      variant="contained"
                      disabled={isGenerating || !formCategory || !canGenerate}
                      onClick={handleAiGenerate}
                      startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : <SmartToyIcon />}
                      sx={{ bgcolor: COLORS.primary, py: 1.5, fontWeight: 600, textTransform: 'none' }}
                    >
                      {isGenerating ? 'Génération en cours...' : 'Générer le brouillon'}
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>Nom <span style={{ color: 'red' }}>*</span></Typography>
                      <TextField fullWidth size="small" placeholder="ex: Promo Janvier" value={formName} onChange={e => setFormName(e.target.value)} required />
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>Catégorie <span style={{ color: 'red' }}>*</span></Typography>
                      <Select
                        fullWidth
                        size="small"
                        value={formCategory}
                        onChange={e => setFormCategory(e.target.value)}
                        displayEmpty
                        renderValue={(selected) => {
                          if (selected === '') {
                            return <em style={{ color: COLORS.textMuted, fontStyle: 'normal' }}>Sélectionner une catégorie</em>;
                          }
                          const cat = categories.find(c => c.id === selected);
                          return cat ? (cat.title || cat.name) : selected;
                        }}
                      >
                        <MenuItem disabled value="">
                          <em>Sélectionner une catégorie</em>
                        </MenuItem>
                        {categories
                          .filter(cat => ['facebook', 'whatsapp', 'instagram', 'sms'].includes((cat.title || cat.name || '').toLowerCase()))
                          .map(cat => (
                            <MenuItem key={cat.id} value={cat.id}>{cat.title || cat.name || `Catégorie ${cat.id}`}</MenuItem>
                          ))}
                      </Select>
                    </Box>

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', display: 'block' }}>Message <span style={{ color: 'red' }}>*</span></Typography>
                        <Tooltip
                          title={<DynamicFieldsHelper onCopy={handleCopyTag} />}
                          placement="right"
                          arrow
                          interactive
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help', color: COLORS.textMuted, '&:hover': { color: COLORS.primary }, ml: 1 }}>
                            <HelpOutlineIcon sx={{ fontSize: 16 }} />
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Variables dynamiques</Typography>
                          </Box>
                        </Tooltip>
                      </Box>
                      <TextField fullWidth multiline rows={4} size="small" value={formMessage} onChange={e => setFormMessage(e.target.value)} placeholder="Contenu du message..." helperText="Conseil: restez concis, le message est limité en longueur." required />
                    </Box>

                    {/* Sélection d'une image d'asset Mautic (optionnelle) */}
                    {(() => {
                      const selectedCat = categories.find(c => c.id === formCategory);
                      const isSms = selectedCat && (selectedCat.title || selectedCat.name || '').toLowerCase() === 'sms';

                      if (!isSms) {
                        return (
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>Image liée (optionnel)</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <TextField
                                value={selectedAsset ? `${selectedAsset.name} (ID: ${selectedAsset.id})` : ''}
                                fullWidth
                                size="small"
                                InputProps={{
                                  readOnly: true,
                                }}
                                placeholder="Aucune image sélectionnée"
                              />
                              <Tooltip title="Choisir une image">
                                <IconButton
                                  onClick={handleOpenAssetGallery}
                                  sx={{
                                    border: `1px solid ${COLORS.border}`,
                                    borderRadius: 1,
                                    color: COLORS.primary
                                  }}
                                >
                                  <ImageIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Typography variant="caption" sx={{ color: COLORS.textMuted, display: 'block', mt: 0.5 }}>
                              Sélectionnez une image à associer à ce message. L'ID sera ajouté au titre.
                            </Typography>
                          </Box>
                        );
                      }
                      return null;
                    })()}

                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formPublished}
                            onChange={(e) => setFormPublished(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Message actif"
                      />
                    </Box>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, pt: 3, borderTop: `1px solid ${COLORS.border}` }}>
                <Button onClick={() => setCreationMode(null)} sx={{ color: COLORS.textMuted }}>Retour</Button>
                <Button
                  onClick={creationMode === 'ai' ? () => setCreationMode('manual') : handleSave}
                  disabled={(creationMode === 'ai' && !formMessage) || saving}
                  variant="contained"
                  sx={{ bgcolor: COLORS.primary }}
                >
                  {saving ? <CircularProgress size={24} color="inherit" /> : (creationMode === 'ai' ? 'Utiliser ce brouillon' : 'Enregistrer')}
                </Button>
              </Box>
            </Box>

            {/* Right: Preview */}
            <Box sx={{ width: '45%', bgcolor: COLORS.panelBg, borderLeft: `1px solid ${COLORS.border}`, p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative' }}>
              <Typography
                variant="subtitle2"
                sx={{ color: COLORS.textMuted, fontWeight: 700, textTransform: 'uppercase', mb: 3, textAlign: 'center' }}
              >
                {creationMode === 'ai' ? 'Aperçu IA' : 'Aperçu en direct'}
              </Typography>
              {creationMode === 'ai' && (
                <Box
                  onMouseEnter={() => setEditTooltipOpen(true)}
                  onMouseLeave={() => setEditTooltipOpen(false)}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                  }}
                >
                  <IconButton
                    size="medium"
                    onClick={() => setCreationMode('manual')}
                    disabled={!formMessage.trim()}
                    sx={{
                      color: COLORS.primary,
                      bgcolor: COLORS.white,
                      boxShadow: '0 2px 4px rgba(15, 23, 42, 0.18)',
                      '&:hover': {
                        bgcolor: COLORS.primaryLight
                      }
                    }}
                  >
                    <EditIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                  {editTooltipOpen && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        mt: 0.5,
                        bgcolor: 'rgba(15, 23, 42, 0.92)',
                        color: '#fff',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.72rem',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 4px 10px rgba(15, 23, 42, 0.4)',
                        zIndex: 10,
                      }}
                    >
                      Passer en édition manuelle
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <SmsMockup
                  message={formMessage || (creationMode === 'ai' ? (isGenerating ? 'Génération...' : 'Décrivez votre besoin à gauche.') : 'Votre message ici...')}
                  image={(() => {
                    const selectedCat = categories.find(c => c.id === formCategory);
                    const isSms = selectedCat && (selectedCat.title || selectedCat.name || '').toLowerCase() === 'sms';
                    return !isSms ? selectedAsset?.url : null;
                  })()}
                />
              </Box>

              <Typography variant="caption" sx={{ textAlign: 'center', color: COLORS.textMuted, mt: 4, display: 'block' }}>
                L'apparence peut varier selon l'appareil.
              </Typography>
            </Box>
          </Box>
        )}
      </Dialog>

      {/* --- Edit Modal --- */}
      <Dialog open={dialogOpen && editingItem !== null} onClose={handleDialogClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', height: '650px', maxWidth: '1000px' } }}>
        <Box sx={{ display: 'flex', height: '100%' }}>
          {/* Left: Form */}
          <Box sx={{ flex: 1, p: 4, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Modifier le message</Typography>
              <Typography variant="body2" sx={{ color: COLORS.textMuted, mt: 0.5 }}>Modifiez les détails de votre message.</Typography>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>Nom <span style={{ color: 'red' }}>*</span></Typography>
                <TextField fullWidth size="small" placeholder="ex: Promo Janvier" value={formName} onChange={e => setFormName(e.target.value)} required />
              </Box>

              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>Catégorie <span style={{ color: 'red' }}>*</span></Typography>
                <Select
                  fullWidth
                  size="small"
                  value={formCategory}
                  onChange={e => setFormCategory(e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected === '') {
                      return <em style={{ color: COLORS.textMuted, fontStyle: 'normal' }}>Sélectionner une catégorie</em>;
                    }
                    const cat = categories.find(c => c.id === selected);
                    return cat ? (cat.title || cat.name) : selected;
                  }}
                >
                  <MenuItem disabled value="">
                    <em>Sélectionner une catégorie</em>
                  </MenuItem>
                  {categories
                    .filter(cat => ['facebook', 'whatsapp', 'instagram', 'sms'].includes((cat.title || cat.name || '').toLowerCase()))
                    .map(cat => (
                      <MenuItem key={cat.id} value={cat.id}>{cat.title || cat.name || `Catégorie ${cat.id}`}</MenuItem>
                    ))}
                </Select>
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', display: 'block' }}>Message <span style={{ color: 'red' }}>*</span></Typography>
                  <Tooltip
                    title={<DynamicFieldsHelper onCopy={handleCopyTag} />}
                    placement="right"
                    arrow
                    interactive
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help', color: COLORS.textMuted, '&:hover': { color: COLORS.primary }, ml: 1 }}>
                      <HelpOutlineIcon sx={{ fontSize: 16 }} />
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>Variables dynamiques</Typography>
                    </Box>
                  </Tooltip>
                </Box>
                <TextField fullWidth multiline rows={4} size="small" value={formMessage} onChange={e => setFormMessage(e.target.value)} placeholder="Contenu du message..." helperText="Conseil: restez concis, le message est limité en longueur." required />
              </Box>

              {/* Sélection d'une image d'asset Mautic (optionnelle) */}
              {(() => {
                const selectedCat = categories.find(c => c.id === formCategory);
                const isSms = selectedCat && (selectedCat.title || selectedCat.name || '').toLowerCase() === 'sms';

                if (!isSms) {
                  return (
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', mb: 0.5, display: 'block' }}>Image liée (optionnel)</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          value={selectedAsset ? `${selectedAsset.name} (ID: ${selectedAsset.id})` : ''}
                          fullWidth
                          size="small"
                          InputProps={{
                            readOnly: true,
                          }}
                          placeholder="Aucune image sélectionnée"
                        />
                        <Tooltip title="Choisir une image">
                          <IconButton
                            onClick={handleOpenAssetGallery}
                            sx={{
                              border: `1px solid ${COLORS.border}`,
                              borderRadius: 1,
                              color: COLORS.primary
                            }}
                          >
                            <ImageIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" sx={{ color: COLORS.textMuted, display: 'block', mt: 0.5 }}>
                        Sélectionnez une image à associer à ce message. L'ID sera ajouté au titre.
                      </Typography>
                    </Box>
                  );
                }
                return null;
              })()}

              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formPublished}
                      onChange={(e) => setFormPublished(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Message actif"
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, pt: 3, borderTop: `1px solid ${COLORS.border}` }}>
              <Button onClick={handleDialogClose} sx={{ color: COLORS.textMuted }}>Annuler</Button>
              <Button onClick={handleSave} disabled={saving} variant="contained" sx={{ bgcolor: COLORS.primary }}>
                {saving ? <CircularProgress size={24} color="inherit" /> : 'Mettre à jour'}
              </Button>
            </Box>
          </Box>

          {/* Right: Preview */}
          <Box sx={{ width: '45%', bgcolor: COLORS.panelBg, borderLeft: `1px solid ${COLORS.border}`, p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="subtitle2" sx={{ color: COLORS.textMuted, fontWeight: 700, textTransform: 'uppercase', mb: 3, textAlign: 'center' }}>Aperçu en direct</Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <SmsMockup
                message={formMessage || 'Votre message ici...'}
                image={(() => {
                  const selectedCat = categories.find(c => c.id === formCategory);
                  const isSms = selectedCat && (selectedCat.title || selectedCat.name || '').toLowerCase() === 'sms';
                  if (isSms) return null;
                  // Priorité : nouvel asset sélectionné, sinon image existante du message
                  return selectedAsset?.url || editImageUrl || null;
                })()}
              />
            </Box>

            <Typography variant="caption" sx={{ textAlign: 'center', color: COLORS.textMuted, mt: 4, display: 'block' }}>
              L'apparence peut varier selon l'appareil.
            </Typography>
          </Box>
        </Box>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => (deleting ? null : setDeleteConfirmOpen(false))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Supprimer le Message</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Êtes-vous sûr de vouloir supprimer ce Message&nbsp;?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: '#6B7280' }}>
            Cette action supprimera le Message.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={deleting}
          >
            Annuler
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Galerie d'images Mautic pour lier une image au Message */}
      <Dialog
        open={assetGalleryOpen}
        onClose={() => setAssetGalleryOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <Box p={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Bibliothèque d'images Mautic</Typography>
            <Box display="flex" gap={1}>
              {/* Bouton pour uploader une nouvelle image */}
              <Button
                component="label"
                variant="contained"
                startIcon={uploadingImage ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                disabled={uploadingImage}
                size="small"
                sx={{
                  bgcolor: '#007B8A',
                  '&:hover': {
                    bgcolor: '#006270',
                  },
                }}
              >
                {uploadingImage ? 'Upload en cours...' : 'Uploader une image'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleUploadImage}
                />
              </Button>
              <IconButton onClick={() => setAssetGalleryOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {assetImages.length === 0 && !assetGalleryLoading ? (
            <Typography color="textSecondary" textAlign="center" py={4}>
              Aucune image disponible pour l'instant.
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 2,
                maxHeight: 500,
                overflowY: 'auto',
                pb: 2,
              }}
              onScroll={(e) => {
                const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                if (scrollHeight - scrollTop <= clientHeight + 50) {
                  handleLoadMoreAssets();
                }
              }}
            >
              {assetImages.map((asset) => (
                <Paper
                  key={asset.id}
                  elevation={selectedAsset?.id === asset.id ? 6 : 2}
                  sx={{
                    cursor: 'pointer',
                    overflow: 'hidden',
                    aspectRatio: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border:
                      selectedAsset?.id === asset.id
                        ? '2px solid #007B8A'
                        : '1px solid #E5E7EB',
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'scale(1.02)',
                    },
                  }}
                  onClick={() => handleSelectAsset(asset)}
                >
                  <img
                    src={asset.url}
                    alt={asset.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Paper>
              ))}
            </Box>
          )}

          {assetGalleryLoading && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={30} />
            </Box>
          )}

          {!assetGalleryLoading && !assetGalleryHasMore && assetImages.length > 0 && (
            <Typography color="textSecondary" textAlign="center" py={2} variant="caption">
              Toutes les images ont été chargées.
            </Typography>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default TextMessages;

